import React from 'react';
import {
  CircleDot,
  CircleSlash,
  CircleX,
  CreditCard,
  GitFork,
  Globe,
  Hammer,
  Link as LinkIcon,
  Plus,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Trash2,
  Unlink,
  type LucideIcon,
} from 'lucide-react';

import type { SiteEvent } from '@/hooks/useSiteEvents';

/**
 * Activity-log descriptor registry.
 *
 * One entry per `eventType` the platform emits. Each descriptor returns:
 *   - `Icon` — a lucide icon component to render in the timeline dot.
 *   - `verb` — sentence fragment that follows the actor name. Returns
 *     React.ReactNode so descriptors can pull values out of `metadata`
 *     (e.g. domain name) and bold them inline.
 *   - `iconTone` — optional. Use sparingly — GitHub-style: icons stay
 *     neutral except for genuinely meaningful "moments" (creation, etc.).
 *     Status info goes in the right-side badge, not the icon.
 *
 * Adding a new event type is two changes:
 *   1. Extend the union in `backend/src/services/audit.types.ts`.
 *   2. Add an entry below.
 *
 * Unknown event types fall back to `DEFAULT_DESCRIPTOR` — a generic dot
 * icon and the raw event_type rendered as inline code. That keeps the UI
 * safe if a new event type ships before the descriptor lands here.
 */

export type IconTone = 'neutral' | 'success';

export interface EventDescriptor {
  Icon: LucideIcon;
  verb: (e: SiteEvent) => React.ReactNode;
  /** Override icon tone. Default is neutral; reserve `success` for genuine
   *  "creation moments" worth visually highlighting. */
  iconTone?: IconTone;
}

const meta = (e: SiteEvent, key: string): string | undefined => {
  const v = e.metadata?.[key];
  return typeof v === 'string' ? v : undefined;
};

const codeFallback = (text: string): React.ReactNode => (
  <code className="text-xs px-1 py-0.5 rounded bg-foreground/5">{text}</code>
);

const REGISTRY: Record<string, EventDescriptor> = {
  // ─── Site lifecycle ─────────────────────────────────────────────────
  // Creation gets a green icon — it's the only meaningful "moment"
  // that earns coloring on this surface. Everything else is neutral.
  'site.created':     { Icon: Plus,   verb: () => <>created this site</>, iconTone: 'success' },
  'site.published':   { Icon: Rocket, verb: () => <>published this site</> },
  'site.republished': { Icon: Rocket, verb: () => <>re-published this site</> },
  'site.deleted':     { Icon: Trash2, verb: () => <>deleted this site</> },

  // ─── Workflow lifecycle (mapped from GH Actions webhooks) ──────────
  'workflow.fork_started':      { Icon: GitFork, verb: () => <>started forking the template repo</> },
  'workflow.fork_succeeded':    { Icon: GitFork, verb: () => <>forked the template repo</> },
  'workflow.fork_failed':       { Icon: GitFork, verb: () => <>had a problem forking the template repo</> },
  'workflow.publish_started':   { Icon: Hammer,  verb: () => <>started publishing the bundle</> },
  'workflow.publish_succeeded': { Icon: Hammer,  verb: () => <>published the bundle</> },
  'workflow.publish_failed':    { Icon: Hammer,  verb: () => <>had a problem publishing the bundle</> },
  'workflow.deploy_started':    { Icon: Rocket,  verb: () => <>started deploying</> },
  'workflow.deploy_succeeded':  { Icon: Rocket,  verb: () => <>deployed the site</> },
  'workflow.deploy_failed':     { Icon: Rocket,  verb: () => <>had a problem deploying</> },
  'workflow.destroy_started':   { Icon: Trash2,  verb: () => <>started tearing down the site</> },
  'workflow.destroy_succeeded': { Icon: Trash2,  verb: () => <>tore down the site</> },
  'workflow.destroy_failed':    { Icon: Trash2,  verb: () => <>had a problem tearing down the site</> },

  // ─── Domain events ──────────────────────────────────────────────────
  // Listed for completeness — these will surface here once the events
  // query joins on metadata.siteId. Strings are already in place.
  'domain.created':  { Icon: Plus,        verb: (e) => <>added domain <strong>{meta(e, 'domain') ?? '—'}</strong></>, iconTone: 'success' },
  'domain.verified': { Icon: ShieldCheck, verb: (e) => <>verified domain <strong>{meta(e, 'domain') ?? '—'}</strong></> },
  'domain.attached': { Icon: LinkIcon,    verb: (e) => <>connected <strong>{meta(e, 'domain') ?? meta(e, 'siteSubdomain') ?? '—'}</strong> to this site</> },
  'domain.detached': { Icon: Unlink,      verb: (e) => <>disconnected <strong>{meta(e, 'domain') ?? '—'}</strong></> },
  'domain.deleted':  { Icon: Globe,       verb: (e) => <>deleted domain <strong>{meta(e, 'domain') ?? '—'}</strong></> },

  // ─── Subscription lifecycle ─────────────────────────────────────────
  // Subscribing is a meaningful "moment" — earns success coloring.
  // Cancellation gets pending outcome upstream so the In-progress badge
  // tells the user the sub stays active until period end.
  'subscription.created':     { Icon: CreditCard,  verb: () => <>subscribed to this site</>, iconTone: 'success' },
  'subscription.canceled':    { Icon: CircleSlash, verb: () => <>canceled the subscription</> },
  'subscription.reactivated': { Icon: RefreshCw,   verb: () => <>reactivated the subscription</> },
  'subscription.deleted':     { Icon: CircleX,     verb: () => <>subscription ended</> },
};

const DEFAULT_DESCRIPTOR: EventDescriptor = {
  Icon: CircleDot,
  verb: (e) => <>recorded {codeFallback(e.eventType)}</>,
};

export function getDescriptor(e: SiteEvent): EventDescriptor {
  return REGISTRY[e.eventType] ?? DEFAULT_DESCRIPTOR;
}

/**
 * Right-side status pill for outcomes that warrant a callout.
 * Returns the props for the shared `<Badge>` primitive so the activity
 * log uses the same component as every other badge in the app.
 *
 * - failure → "Error" (destructive variant — red)
 * - pending → "In progress" (warning variant — amber)
 * - success → null (no badge — successful events are the default state;
 *   keeping the row clean means a badge actually signals something)
 */
export interface StatusBadge {
  label: string;
  variant: 'destructive' | 'warning';
}

export function statusBadgeFor(e: SiteEvent): StatusBadge | null {
  if (e.outcome === 'failure') return { label: 'Error', variant: 'destructive' };
  if (e.outcome === 'pending') return { label: 'In progress', variant: 'warning' };
  return null;
}
